import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import { message, Modal, Spin } from "antd";
import {
  DeleteOutlined,
  CheckOutlined,
  PlusOutlined,
  SearchOutlined,
  SendOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import { UiButton } from "../../ui";
import { formatMoney } from "../../Pharmacy/common/money";
import {
  billedLineAmount,
  billedOrderTotal,
  dispensedPrices,
} from "../../Pharmacy/common/lineMath";
import CgIcon from "../../Pharmacy/common/CgIcon";
import useQuery from "../../../hooks/useQuery";
import { debounce } from "../../../helpers/utility";
import { searchMedicationApi } from "../../../services/medication.services";
import { getCookie } from "../../../helpers/localStorage";
import { FacilityID, ORGANISATONID } from "../../../constants/defaultKeys";
import { detectAdmissionType } from "../ClinicalPractitioner/admissionContext";
import {
  getpatientMedicationsAPI,
  getPatientMedicationStockAPI,
} from "../../../services/patient.services";
import {
  nurseCreatePharmacyOrderApi,
  getNursePharmacyOrdersApi,
  cancelPharmacyOrderApi,
} from "../../../services/pharmacyOrders.services";
import { getPharmacyBillsForAdmissionApi } from "../../../services/pharmacyBilling.services";
import ui from "./nurseOrder.module.css";
/* 2.0 -> "2", 2.5 -> "2.5" - the server sends these as floats. */
const plain = (value) => String(Number(value) || 0);

/*
 * What a line asked for, in the terms it was asked in.
 *
 * An order is placed in whole strips and loose units, and the server adds them
 * up into one figure using the strip the medicine comes in. Printing only that
 * figure left the nurse reading "3" against an order for two strips and a
 * tablet, with no way to tell which three. Both are shown: the ask as it was
 * made, and the total underneath in the units everything else on the row is
 * counted in.
 */
const askedFor = (item) => {
  const strips = Number(item?.requested_strips) || 0;
  const tabs = Number(item?.requested_tabs) || 0;
  const total = Number(item?.requested_qty) || 0;

  if (!strips && !tabs) return { label: plain(total), sub: null };

  const parts = [];
  if (strips) parts.push(`${plain(strips)} ${strips === 1 ? "strip" : "strips"}`);
  if (tabs) parts.push(`${plain(tabs)} ${tabs === 1 ? "tab" : "tabs"}`);

  return {
    label: parts.join(" + "),
    sub: total ? `${plain(total)} units` : null,
  };
};

/* Keystrokes to a count: digits only, never negative. */
const toCount = (value) =>
  Math.max(0, Math.floor(Number(String(value).replace(/\D/g, "")) || 0));

const readStockLevels = (payload) => {
  const rows = Array.isArray(payload)
    ? payload
    : Array.isArray(payload?.data)
      ? payload.data
      : Array.isArray(payload?.results)
        ? payload.results
        : [...(payload?.prescribed || []), ...(payload?.ordered || [])];

  const levels = new Map();
  rows.forEach((row) => {
    const id =
      row?.medicine ?? row?.pharmacy_medicine ?? row?.medicine_id ?? row?.id;
    const qty =
      row?.available_qty ??
      row?.available_quantity ??
      row?.current_stock ??
      row?.stock ??
      row?.quantity;
    if (id == null || qty == null) return;
    levels.set(id, Number(qty) || 0);
  });
  return levels;
};

/*
 * What the search reads, which is the list the doctor prescribes from.
 *
 * The nurse used to search two places - the prescription above and a box for
 * "anything not prescribed" below it - which meant knowing which of the two a
 * medicine would be in before looking for it. There is one search now, and it
 * asks the same endpoint the doctor's own medication search asks.
 */
const searchCatalogue = (text) =>
  searchMedicationApi(
    `query=${encodeURIComponent(String(text || "").toLowerCase())}&organization_id=${getCookie(
      ORGANISATONID
    )}`
  ).then((res) =>
    (res?.data || []).map((item) => ({
      /* The master record's own id, which is what a prescribed line carries
         as `medicine` - so a searched line and a prescribed one are added the
         same way and reach the server in the same shape. */
      medicine: item.record_id ?? item.id,
      name: item.name,
      drug: item.drug1_name || "",
      current_stock: item.current_stock,
    }))
  );

const NursePharmacyOrders = ({ patientData, view: viewProp = "new", onSent }) => {
  const location = useLocation();
  const query = useQuery();
  const admissionType = detectAdmissionType(location.pathname);
  const admissionId = query?.patient;
  const view = viewProp;
  const [prescribed, setPrescribed] = useState([]);
  const [loadingPrescribed, setLoadingPrescribed] = useState(false);
  const [stockLevels, setStockLevels] = useState(new Map());
  const [search, setSearch] = useState("");
  /* What the catalogue search came back with, and whether it is still out. */
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [medicineItems, setMedicineItems] = useState([]);
  const [consumableItems, setConsumableItems] = useState([]);
  const [submitting, setSubmitting] = useState(false);

  const [orders, setOrders] = useState([]);
  const [loadingOrders, setLoadingOrders] = useState(false);

  const [bills, setBills] = useState(new Map());

  const loadOrders = useCallback(() => {
    if (!admissionId) return;
    setLoadingOrders(true);
    const params = new URLSearchParams(
      admissionType === "OP"
        ? { op_consultation: admissionId, page: 1, limit: 50 }
        : { ip_admission: admissionId, page: 1, limit: 50 }
    );
    getNursePharmacyOrdersApi(params)
      .then((res) => setOrders(res?.data?.results || res?.data || []))
      .catch(() => {
        setOrders([]);
        message.error("Failed to load order history");
      })
      .finally(() => setLoadingOrders(false));
  }, [admissionId, admissionType]);

  useEffect(() => {
    if (!admissionId) return;
    setLoadingPrescribed(true);
    const medParams =
      admissionType === "OP" ? `op_consultation=${admissionId}` : `ip_admission=${admissionId}`;
    getpatientMedicationsAPI(medParams)
      .then((res) => {
        const list = (Array.isArray(res?.data) ? res.data : res?.data?.data || [])
          .slice()
          .sort((a, b) => {
            if (a.status === "Started" && b.status !== "Started") return -1;
            if (b.status === "Started" && a.status !== "Started") return 1;
            return 0;
          });

        const seen = new Set();
        const deduped = list.filter((record) => {
          const key = record.medicine || `name-${record.name || record.medicine_name}`;
          if (seen.has(key)) return false;
          seen.add(key);
          return true;
        });

        setPrescribed(deduped);
      })
      .catch(() => setPrescribed([]))
      .finally(() => setLoadingPrescribed(false));
  }, [admissionId, admissionType]);


  useEffect(() => {
    if (!admissionId) return;
    let cancelled = false;

    const params =
      admissionType === "OP" ? `op_consultation=${admissionId}` : `ip_admission=${admissionId}`;

    getPatientMedicationStockAPI(params)
      .then((res) => {
        if (!cancelled) setStockLevels(readStockLevels(res?.data));
      })
      .catch(() => {
        if (!cancelled) setStockLevels(new Map());
      });

    return () => {
      cancelled = true;
    };
  }, [admissionId, admissionType]);

  useEffect(() => {
    if (view === "history") loadOrders();
  }, [view, loadOrders]);

  useEffect(() => {
    if (view !== "history" || !admissionId) return;
    let cancelled = false;

    const params = new URLSearchParams(
      admissionType === "OP"
        ? { op_consultation: admissionId }
        : { ip_admission: admissionId }
    );

    getPharmacyBillsForAdmissionApi(params)
      .then((res) => {
        if (cancelled) return;
        const rows = res?.data?.results || res?.data || [];
        const byKey = new Map();
        rows.forEach((bill) => {
          if (bill?.id != null) byKey.set(bill.id, bill);
          if (bill?.bill_number) byKey.set(bill.bill_number, bill);
        });
        setBills(byKey);
      })
      .catch(() => {
        if (!cancelled) setBills(new Map());
      });

    return () => {
      cancelled = true;
    };
  }, [view, admissionId, admissionType]);

  /*
   * Searching is debounced: the nurse types a name, not a keystroke, and one
   * request per letter would be four requests for "dolo".
   */
  const runSearch = useCallback((text) => {
    if (!String(text || "").trim()) {
      setResults([]);
      setSearching(false);
      return;
    }
    setSearching(true);
    searchCatalogue(text)
      .then((rows) => setResults(rows))
      .catch(() => setResults([]))
      .finally(() => setSearching(false));
  }, []);

  const debouncedSearch = useMemo(() => debounce(runSearch, 300), [runSearch]);

  const isMedicineSelected = (record) => {
    const medicineId = record.medicine;
    const displayName = record.name || record.medicine_name;
    const key = medicineId || `name-${displayName}`;
    return medicineItems.some((item) => (item.medicine || `name-${item.name}`) === key);
  };

  const addMedicineFromTag = (record) => {
    const medicineId = record.medicine;
    const displayName = record.name || record.medicine_name;
    if (!medicineId && !displayName) return;

    setMedicineItems((prev) => {
      const key = medicineId || `name-${displayName}`;
      if (prev.some((item) => (item.medicine || `name-${item.name}`) === key)) {
        return prev.filter((item) => (item.medicine || `name-${item.name}`) !== key);
      }
      return [
        ...prev,
        {
          medicine: medicineId,
          name: displayName,
          drug: record.drug || record.drug1_name,
          /*
           * A line is asked for the way the pharmacy counts it - whole strips
           * and loose units, separately. Which of the two a medicine comes in
           * is the pharmacy's to know, so the nurse states both and the server
           * works out what that is in units.
           */
          requested_strips: 0,
          requested_tabs: 1,
        },
      ];
    });
  };

  const removeItem = (setter, predicate) => {
    setter((prev) => prev.filter((item) => !predicate(item)));
  };

  const itemCount = medicineItems.length + consumableItems.length;
  /* A line with neither a strip nor a loose unit on it is a line asking for
     nothing, so the order waits rather than going up half-filled. */
  const hasQty = (item) =>
    (Number(item.requested_strips) || 0) + (Number(item.requested_tabs) || 0) > 0;
  const canSubmit =
    itemCount > 0 && medicineItems.every(hasQty) && consumableItems.every(hasQty);

  const resetLists = () => {
    setMedicineItems([]);
    setConsumableItems([]);
    setSearch("");
    setResults([]);
  };

  const handleSubmit = () => {
    if (!canSubmit) return;
    setSubmitting(true);
    const payload = {
      sale_type: admissionType === "OP" ? "OP" : "IP",
      ip_admission: admissionType === "OP" ? undefined : Number(admissionId),
      op_consultation: admissionType === "OP" ? Number(admissionId) : undefined,
      facility: Number(getCookie(FacilityID)) || undefined,
      items: [
        ...medicineItems.map((item) => ({
          medicine: item.medicine,
          name: item.name,
          requested_strips: Number(item.requested_strips) || 0,
          requested_tabs: Number(item.requested_tabs) || 0,
        })),
        ...consumableItems.map((item) => ({
          pharmacy_medicine: item.pharmacy_medicine,
          requested_strips: Number(item.requested_strips) || 0,
          requested_tabs: Number(item.requested_tabs) || 0,
        })),
      ],
    };
    nurseCreatePharmacyOrderApi(payload)
      .then((res) => {
        message.success(`Pharmacy order ${res?.data?.order_number} raised`);
        resetLists();
        setSubmitting(false);
        onSent?.();
      })
      .catch((err) => {
        message.error(err?.response?.data?.message || "Failed to raise pharmacy order");
        setSubmitting(false);
      });
  };


  /*
   * The list shows what the doctor prescribed until the nurse searches, and
   * what the search found after that - one place to look either way. A search
   * that has not come back yet keeps showing the prescription rather than
   * emptying the card under the cursor.
   */
  const catalogue = search.trim() ? results : prescribed;

  const draftLines = [
    ...medicineItems.map((item) => ({
      key: item.medicine ? `med-${item.medicine}` : `name-${item.name}`,
      name: item.name,
      strips: item.requested_strips,
      tabs: item.requested_tabs,
      onHand: stockLevels.get(item.medicine),
      setQty: (patch) =>
        setMedicineItems((prev) =>
          prev.map((i) => (i.medicine === item.medicine ? { ...i, ...patch } : i))
        ),
      remove: () => removeItem(setMedicineItems, (i) => i.medicine === item.medicine),
    })),
    ...consumableItems.map((item) => ({
      key: `cons-${item.pharmacy_medicine}`,
      name: item.name,
      sub: "Consumable",
      strips: item.requested_strips,
      tabs: item.requested_tabs,
      setQty: (patch) =>
        setConsumableItems((prev) =>
          prev.map((i) =>
            i.pharmacy_medicine === item.pharmacy_medicine ? { ...i, ...patch } : i
          )
        ),
      remove: () =>
        removeItem(setConsumableItems, (i) => i.pharmacy_medicine === item.pharmacy_medicine),
    })),
  ];

  const renderNewOrder = () => (
    <div className={ui.wrap}>
      <section className={ui.card}>
        <div className={ui.cardHead}>
          <h3>Add items</h3>
          <span className={ui.cnt}>{prescribed.length}</span>
        </div>

        <div className={ui.srchBar}>
          <div className={ui.srch}>
            <SearchOutlined />
            <input
              value={search}
              placeholder="Search medicines and consumables"
              onChange={(e) => {
                setSearch(e.target.value);
                debouncedSearch(e.target.value);
              }}
            />
            {searching ? (
              <span className={ui.srchSpin}>
                <Spin size="small" />
              </span>
            ) : null}
          </div>
        </div>

        <div className={ui.pHdr}>
          <div>Item</div>
          <div>On hand</div>
          <div style={{ justifySelf: "end" }}>Add</div>
        </div>

        <div className={ui.pRows}>
          {loadingPrescribed ? (
            <div className={ui.empty}>
              <Spin size="small" />
            </div>
          ) : catalogue.length ? (
            catalogue.map((record) => {
              const name = record.name || record.medicine_name;
              const selected = isMedicineSelected(record);

              /*
               * Stock is known for the patient's own prescription, which the
               * screen asks about by name; a searched medicine brings its own
               * figure back with it.
               */
              const available = stockLevels.has(record.medicine)
                ? stockLevels.get(record.medicine)
                : record.current_stock;
              const knowsStock = available != null;

              return (
                <div
                  key={record.id ?? `med-${record.medicine}` ?? name}
                  className={`${ui.pRow} ${selected ? ui.on : ""}`.trim()}
                >
                  <div className={ui.pN} title={name}>
                    {name}
                    {record.status === "Started" ? <i>STARTED</i> : null}
                  </div>

                  <div
                    className={[
                      ui.avl,
                      !knowsStock ? ui.avlUnknown : available <= 0 ? ui.avlNone : available <= 3 ? ui.avlLow : "",
                    ]
                      .filter(Boolean)
                      .join(" ")}
                  >
                    {knowsStock ? available : "—"}
                    {knowsStock && available <= 0 ? <small>none left</small> : null}
                  </div>

                  {/*
                    * A medicine the doctor typed by hand has no record behind
                    * it, only a name - and that is enough to order it, since
                    * the name is what goes up on the line. It is added like
                    * any other rather than being offered a button that does
                    * nothing.
                    */}
                  <button
                    type="button"
                    className={`${ui.addB} ${selected ? ui.on : ""}`.trim()}
                    onClick={() => addMedicineFromTag(record)}
                  >
                    {selected ? <CheckOutlined /> : <PlusOutlined />}
                    {selected ? "Added" : "Add"}
                  </button>
                </div>
              );
            })
          ) : (
            <div className={ui.empty}>
              {search.trim()
                ? `Nothing in the catalogue matches \u201c${search.trim()}\u201d`
                : "Nothing prescribed yet - search above for anything else"}
            </div>
          )}
        </div>

      </section>

      <section className={ui.card}>
        <div className={ui.cardHead}>
          <h3>Order list</h3>
          <span className={ui.cnt}>
            {itemCount} item{itemCount === 1 ? "" : "s"}
          </span>
          <span className={ui.sp} />
          {itemCount ? (
            <UiButton size="sm" variant="secondary" onClick={resetLists}>
              Clear
            </UiButton>
          ) : null}
        </div>

        {draftLines.length ? (
          draftLines.map((line) => (
            <div key={line.key} className={ui.line}>
              <div className={ui.lnN} title={line.name}>
                {line.name}
                <span className={ui.lnS}>
                  {line.sub ?? (line.onHand != null ? `${line.onHand} on hand` : "")}
                </span>
              </div>

              {/*
                * Asked for the way the pharmacy counts it: whole strips and
                * loose units, stated separately. How many a strip holds is the
                * pharmacy's to know, so neither side has to do the division.
                */}
              <div className={ui.qtyPair}>
                <label className={ui.qtyBox}>
                  <span>Strips</span>
                  <input
                    value={line.strips ?? 0}
                    inputMode="numeric"
                    aria-label={`Strips of ${line.name}`}
                    onChange={(e) =>
                      line.setQty({ requested_strips: toCount(e.target.value) })
                    }
                  />
                </label>
                <label className={ui.qtyBox}>
                  <span>Tabs</span>
                  <input
                    value={line.tabs ?? 0}
                    inputMode="numeric"
                    aria-label={`Loose units of ${line.name}`}
                    onChange={(e) =>
                      line.setQty({ requested_tabs: toCount(e.target.value) })
                    }
                  />
                </label>
              </div>

              <button
                type="button"
                className={ui.bin}
                title="Remove"
                aria-label={`Remove ${line.name}`}
                onClick={line.remove}
              >
                <DeleteOutlined />
              </button>
            </div>
          ))
        ) : (
          <div className={ui.empty}>Nothing added yet — use Add on the list.</div>
        )}

        <div className={ui.foot}>
          <span className={ui.hint}>
            {itemCount
              ? `${itemCount} item${itemCount === 1 ? "" : "s"} ready`
              : "Add at least one item to send"}
          </span>
          <span className={ui.sp} />
          <UiButton
            disabled={!canSubmit}
            loading={submitting}
            icon={<SendOutlined />}
            onClick={handleSubmit}
          >
            Send to pharmacy
          </UiButton>
        </div>
      </section>
    </div>
  );


  const [openOrders, setOpenOrders] = useState({});
  const toggleOrder = (id) =>
    setOpenOrders((prev) => ({ ...prev, [id]: !prev[id] }));

 
  const isBilledOrder = (order) =>
    order.status === "completed" || Boolean(order.bill_number || order.bill);

  const statusTag = (order) => {
    if (order.status === "cancelled") {
      return <span className={`${ui.tag} ${ui.tagCancel}`}>Cancelled</span>;
    }
    if (isBilledOrder(order)) {
      return <span className={`${ui.tag} ${ui.tagBilled}`}>Billed</span>;
    }
    return <span className={`${ui.tag} ${ui.tagSent}`}>Pending</span>;
  };


  const billOf = (order) =>
    bills.get(order.bill) ?? bills.get(order.bill_number) ?? null;

  const orderAmount = (order, bill) => {
    const sent =
      bill?.grand_total ??
      order.total_amount ??
      order.net_amount ??
      order.grand_total ??
      order.amount;
    if (sent != null) return Number(sent);
    return isBilledOrder(order) ? billedOrderTotal(order) : 0;
  };
  const billedByLine = (order, bill) => {
    const who =
      bill?.created_by_name ?? bill?.billed_by_name ?? bill?.cashier_name;
    const when = bill?.created_at ?? order.billed_at;
    if (when || who) {
      return [
        who ? `Billed by ${who}` : "Billed",
        when ? dayjs(when).format("DD MMM YYYY · hh:mm A") : null,
      ]
        .filter(Boolean)
        .join(" · ");
    }

    const dispense = (order.dispenses || []).find((d) => d.dispensed_at);
    if (!dispense) return null;
    return [
      dispense.dispensed_by_name
        ? `Dispensed by ${dispense.dispensed_by_name}`
        : "Dispensed",
      dayjs(dispense.dispensed_at).format("DD MMM YYYY · hh:mm A"),
    ].join(" · ");
  };

  /* Nothing has gone out yet, so there is still a request to withdraw. */
  const canCancelRequest = (order) =>
    !isBilledOrder(order) &&
    order.status !== "cancelled" &&
    !(order.items || []).some((item) => Number(item.dispense_qty) > 0);

  const handleCancelRequest = (order) => {
    Modal.confirm({
      title: `Cancel ${order.order_number || "this request"}?`,
      content:
        "The pharmacy stops working on it. Raise a new order if the ward still needs the medicines.",
      okText: "Cancel request",
      okButtonProps: { danger: true },
      cancelText: "Keep it",
      onOk: () =>
        cancelPharmacyOrderApi(order.id)
          .then(() => {
            message.success(`${order.order_number || "Request"} cancelled`);
            loadOrders();
          })
          .catch((err) =>
            message.error(
              err?.response?.data?.message || "Failed to cancel the request"
            )
          ),
    });
  };

  const renderHistory = () => {
    if (loadingOrders) {
      return (
        <div style={{ padding: 48, textAlign: "center" }}>
          <Spin />
        </div>
      );
    }

    return (
      <section className={ui.card}>
        <div className={ui.cardHead}>
          <h3>Order history</h3>
          <span className={ui.cnt}>{orders.length}</span>
        </div>

        {orders.length ? (
          <div className={ui.hist}>
            {orders.map((order) => {
              const isOpen = openOrders[order.id] ?? false;
              const items = order.items || [];
              const supplied = items.some((item) => Number(item.dispense_qty) > 0);
              const billed = isBilledOrder(order);
              const bill = billOf(order);
              const amount = orderAmount(order, bill);
              /* The price each line actually went out at, off the batch it
                 left on. */
              const prices = dispensedPrices(order);

              return (
                <div
                  key={order.id}
                  className={`${ui.ord} ${isOpen ? ui.ordOpen : ""}`.trim()}
                >
                  <button
                    type="button"
                    className={ui.ordH}
                    aria-expanded={isOpen}
                    onClick={() => toggleOrder(order.id)}
                  >
                    <span className={ui.chev}>
                      <CgIcon name="chevRight" size={16} stroke={2} />
                    </span>
                    <span>
                      <span className={`${ui.ordId} ${ui.mono}`}>{order.order_number}</span>{" "}
                      &nbsp;
                      {statusTag(order)}
                      <div className={ui.ordSub}>
                        {items.length} item{items.length === 1 ? "" : "s"}
                        {order.requested_by_name
                          ? ` · order by ${order.requested_by_name}`
                          : ""}
                      </div>
                    </span>
                    <span className={ui.sp} />
                    {amount > 0 ? (
                      <span className={ui.ordAmt}>{formatMoney(amount)}</span>
                    ) : null}
                    <span className={ui.ordWhen}>
                      {order.created_at
                        ? dayjs(order.created_at).format("DD MMM YYYY · hh:mm A")
                        : ""}
                    </span>
                  </button>

                  {isOpen ? (
                    <div className={ui.ordB}>
                      <table className={ui.tbl}>
                        <thead>
                          <tr>
                            <th>Item</th>
                            <th className={ui.r}>Asked</th>
                            {/* Once anything has gone out, what was asked for
                                and what arrived are different questions. */}
                            {supplied ? <th className={ui.r}>Supplied</th> : null}
                            {billed ? <th className={ui.r}>Billed</th> : null}
                          </tr>
                        </thead>
                        <tbody>
                          {items.map((item) => {
                            const name =
                              item.pharmacy_medicine_name || item.medicine_name || item.name;
                            const got = Number(item.dispense_qty) || 0;
                            const asked = Number(item.requested_qty) || 0;
                            const ask = askedFor(item);
                            const short = supplied && got < asked;
                            const lineBilled = billed
                              ? billedLineAmount(item, prices.get(item.id))
                              : null;

                            return (
                              <tr key={item.id} className={supplied && !got ? ui.gone : undefined}>
                                <td>
                                  <span className={ui.lnN}>{name}</span>
                                  {item.is_replaced && item.original_pharmacy_medicine_name ? (
                                    <div className={`${ui.note} ${ui.noteSub}`}>
                                      <CgIcon name="swap" size={13} stroke={2} />
                                      Pharmacy gave this in place of{" "}
                                      {item.original_pharmacy_medicine_name}
                                      {/* The substitute's own dose, where the
                                          pharmacy set one - it is what the
                                          patient is to take, and it is rarely
                                          the dose that was prescribed. */}
                                      {item.replaced_dose ? ` · ${item.replaced_dose}` : ""}
                                    </div>
                                  ) : null}
                                  {supplied && !got ? (
                                    <div className={`${ui.note} ${ui.noteMiss}`}>
                                      {billed
                                        ? "Not supplied — not billed"
                                        : "Not supplied"}
                                    </div>
                                  ) : short ? (
                                    <div className={ui.note}>
                                      {plain(asked - got)} units short &mdash; only{" "}
                                      {plain(got)} supplied
                                    </div>
                                  ) : null}
                                </td>
                                <td className={ui.r}>
                                  <span className={ui.qty}>{ask.label}</span>
                                  {ask.sub ? (
                                    <span className={ui.qtyS}>{ask.sub}</span>
                                  ) : null}
                                </td>
                                {supplied ? <td className={ui.r}>{plain(got)}</td> : null}
                                {billed ? (
                                  <td className={ui.r}>
                                    {lineBilled == null ? "—" : formatMoney(lineBilled)}
                                  </td>
                                ) : null}
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>

                      {/* One line under the table for each of the two states:
                          what it was billed as, or that it is still out. */}
                      {billed ? (
                        <div className={ui.ordF}>
                          <CgIcon name="billing" size={15} stroke={2} />
                          <span>
                            {order.bill_number || bill?.bill_number ? (
                              <b className={ui.mono}>
                                {order.bill_number || bill.bill_number}
                              </b>
                            ) : null}
                            {(order.bill_number || bill?.bill_number) && amount > 0
                              ? " · "
                              : null}
                            {amount > 0 ? formatMoney(amount) : null}
                          </span>
                          {billedByLine(order, bill) ? (
                            <span>{billedByLine(order, bill)}</span>
                          ) : null}
                          <span className={ui.sp} />
                          <span>Added to the admission bill</span>
                        </div>
                      ) : order.status === "cancelled" ? (
                        <div className={ui.ordF}>Cancelled before the pharmacy billed it.</div>
                      ) : (
                        <div className={ui.wait}>
                          <span className={ui.pulse} />
                          <span>
                            With the pharmacy &mdash; it shows here as billed once supplied.
                            Nothing for you to do.
                          </span>
                          <span className={ui.sp} />
                          {canCancelRequest(order) ? (
                            <UiButton
                              size="sm"
                              variant="secondary"
                              onClick={() => handleCancelRequest(order)}
                            >
                              Cancel request
                            </UiButton>
                          ) : null}
                        </div>
                      )}
                    </div>
                  ) : null}
                </div>
              );
            })}
          </div>
        ) : (
          <div className={ui.empty}>No pharmacy orders raised for this patient yet.</div>
        )}
      </section>
    );
  };

  return view === "new" ? renderNewOrder() : renderHistory();
};

export default NursePharmacyOrders;
